import Keycloak from 'keycloak-js';
import { KEYCLOAK_CONFIG } from '../config/apiConfig';

const keycloakConfig = {
    url: KEYCLOAK_CONFIG.url,
    realm: KEYCLOAK_CONFIG.realm,
    clientId: KEYCLOAK_CONFIG.clientId,
};

const keycloak = new Keycloak(keycloakConfig);

/**
 * Initialize Keycloak and join to the property window of the browser
 * @param {Function} onAuthenticatedCallback
 */
export const initKeycloak = (onAuthenticatedCallback, onErrorCallback) => {
    // Check if Keycloak is already initialized by checking if authenticated property exists
    // This happens when the component re-mounts (e.g., during hot reload or navigation)
    if (keycloak.authenticated !== undefined) {
        console.log('Keycloak already initialized, skipping re-initialization');
        if (onAuthenticatedCallback) onAuthenticatedCallback();
        return;
    }

    const token = localStorage.getItem('kc_token');
    const refreshToken = localStorage.getItem('kc_refreshToken');
    const idToken = localStorage.getItem('kc_idToken');

    const initOptions = {
        pkceMethod: 'S256',
    };

    // If we have tokens, use them and skip onLoad redirect/check
    // This is crucial for ROPC flow where we don't have a Keycloak session cookie
    if (token) {
        initOptions.token = token;
        initOptions.refreshToken = refreshToken;
        initOptions.idToken = idToken;
        console.log('Keycloak: initializing with stored tokens');
    } else {
        // Only use check-sso if we don't have tokens
        initOptions.onLoad = 'check-sso';
        initOptions.silentCheckSsoRedirectUri = window.location.origin + '/silent-check-sso.html';
        console.log('Keycloak: initializing with check-sso');
    }

    keycloak
        .init(initOptions)
        .then(async (authenticated) => {
            if (authenticated) {
                console.log('User is authenticated!');
            } else {
                console.log('Keycloak init returned unauthenticated.');
            }

            // Proactively attempt token refresh if stored refresh token is present
            if (refreshToken) {
                try {
                    const { authService } = await import('./authService');
                    const refreshed = await authService.updateToken(0);
                    if (refreshed) {
                        console.log('Keycloak token successfully refreshed during initKeycloak!');
                    }
                } catch (refErr) {
                    console.warn('Proactive refresh during initKeycloak encountered an issue:', refErr);
                }
            }

            onAuthenticatedCallback();
        })
        .catch((error) => {
            console.error('Keycloak init error:', error);
            if (onErrorCallback) onErrorCallback(error);
        });
};

export const doLogin = keycloak.login;

export const doLogout = keycloak.logout;

export const getToken = () => keycloak.token;

export const getParsedToken = () => keycloak.tokenParsed;

export const updateToken = (successCallback) =>
    keycloak.updateToken(5).then(successCallback).catch(doLogin);

export const getUsername = () => keycloak.tokenParsed?.preferred_username;

export const hasRole = (roles) => roles.some((role) => keycloak.hasRealmRole(role));

export default keycloak;
